import transform from 'lodash/transform';
import set from 'lodash/set';
import unset from 'lodash/unset';
import every from 'lodash/every';
import isObject from 'lodash/isObject';
import isPlainObject from 'lodash/isPlainObject';
import forEach from 'lodash/forEach';
import isEmpty from 'lodash/isEmpty';
import isString from 'lodash/isString';
import mapValues from 'lodash/mapValues';
import forOwn from 'lodash/forOwn';
import isNull from 'lodash/isNull';

export const isObjectEmpty = (inputObject) =>
  !Object.keys(inputObject || {}).length;

export const getKeyByValue = (map: Map<any, any>, value) => {
  for (let [key, val] of map.entries()) {
    if (val === value) {
      return key;
    }
  }
};

export const arrayInObject = (obj) => {
  if (Array.isArray(obj)) {
    return true;
  }

  if (typeof obj === 'object' && obj !== null) {
    for (const value of Object.values(obj)) {
      if (arrayInObject(value)) {
        return true;
      }
    }
  }

  return false;
};

export const filterObjectKeys = (obj = {}, keyString) => {
  if (typeof obj !== 'object' || obj === null) {
    return [];
  }

  if (!keyString) {
    return Object.keys(obj);
  }
  const keys = keyString?.split('.')?.filter((key) => key !== '');

  let currentObj = obj;
  // Check if the parent keys exist in the object and are objects themselves
  for (let i = 0; i < keys?.length - 1; i++) {
    const key = keys?.[i];

    if (
      !currentObj?.hasOwnProperty(key) ||
      typeof currentObj[key] !== 'object' ||
      currentObj[key] === null
    ) {
      return [];
    }
    currentObj = currentObj?.[key];
  }

  // Check if the last key exists and is an object
  const lastKey = keys?.[keys?.length - 1];

  if (
    !currentObj?.hasOwnProperty(lastKey) ||
    !currentObj?.[lastKey] ||
    typeof currentObj[lastKey] !== 'object' ||
    currentObj[lastKey] === null
  ) {
    return [];
  }
  // Return the keys of the last object as an array
  return Object.keys(currentObj?.[lastKey]);
};

export const isStringifiedObject = (str) => {
  try {
    const obj = JSON.parse(str);
    return typeof obj === 'object' && obj !== null;
  } catch (error) {
    return false;
  }
};

export const flattenObject = (obj) => {
  return transform(
    obj,
    (result, value, key) => {
      if (isPlainObject(value) && !isEmpty(value)) {
        const flattenedValue = flattenObject(value);
        forEach(flattenedValue, (subValue, subKey) => {
          result[`${key.toString()}.${subKey}`] = subValue;
        });
      } else {
        result[key] = value;
      }
    },
    {},
  );
};

export const unflattenObject = (flatObj) => {
  return transform(
    flatObj,
    (result, value, key: any) => {
      set(result, key, value);
    },
    {},
  );
};

export const deleteNestedKey = (obj, keyToDelete) => {
  const keys = keyToDelete.split('.');
  const lastKey = keys.pop();
  const parentKey = keys.join('.');

  if (parentKey === '') {
    delete obj[lastKey];
  } else {
    unset(obj, parentKey + '.' + lastKey);
  }
};

const isPrimitive = (value: any) => {
  return !isObject(value);
};

export const containsOnlyPrimitives = (array) => {
  return every(array, isPrimitive);
};

export const isParsableObject = (value) => {
  if (Array.isArray(value)) {
    return true;
  }

  if (typeof value === 'string') {
    try {
      const parsedValue = JSON.parse(value);
      if (
        Array.isArray(parsedValue) ||
        (parsedValue &&
          typeof parsedValue === 'object' &&
          !Array.isArray(parsedValue)) ||
        !parsedValue
      ) {
        return true;
      }
    } catch (e) {}
  } else if (typeof value === 'object' && !Array.isArray(value)) {
    return true;
  }
  return false;
};

export function convertObjectToMap(obj) {
  const resultMap = new Map();
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      resultMap.set(key, obj[key]);
    }
  }
  return resultMap;
}

export function mapOjectFlattenedKeys(obj, parentKey = '') {
  let result = {};

  for (let key in obj) {
    let currentKey = parentKey + key;

    if (obj[key] && typeof obj[key] === 'object') {
      const nestedResult = mapOjectFlattenedKeys(obj[key], currentKey + '.');
      if (Object.keys(nestedResult).length === 0) {
        result[key] = currentKey;
      } else {
        Object.assign(result, nestedResult);
      }
    } else {
      result[key] = currentKey;
    }
  }

  return result;
}

export function removeNullValues(obj) {
  // Use _.forOwn to iterate over own enumerable string-keyed properties
  forOwn(obj, (value, key) => {
    // If the value is an object, recursively call removeNullValues
    if (isObject(value)) {
      removeNullValues(value);
    }
    // If the value is null, delete the key from the object
    if (isNull(value) || value === '' || (isObject(value) && isEmpty(value))) {
      delete obj[key];
    }
  });
  // Return the modified object
  return obj;
}

export const trimObjectValues = (obj) =>
  mapValues(obj, (value) => {
    if (isString(value)) {
      return value.trim();
    }
    if (isObject(value)) {
      return trimObjectValues(value);
    }

    return value;
  });
